import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { PiCrownSimpleFill } from "react-icons/pi";
import Button from '../components/Button';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const Title = styled.h1`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${({ theme, $isLimit }) => $isLimit ? theme.colors.error : theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.9rem;
`;

const LimitIndicator = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.8;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const UpgradeSection = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => `${theme.colors.primary}10`};
  border-radius: 12px;
`;

const UpgradeButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Usage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isPremium } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get rooms count
        const roomsQuery = query(
          collection(db, 'rooms'),
          where('userId', '==', user.uid)
        );
        const roomsSnapshot = await getDocs(roomsQuery);
        const roomsCount = roomsSnapshot.docs.length;

        // Get bins per room
        const binsPerRoom = {};
        for (const roomDoc of roomsSnapshot.docs) {
          const binsQuery = query(
            collection(db, 'bins'),
            where('roomId', '==', roomDoc.id)
          );
          const binsSnapshot = await getDocs(binsQuery);
          binsPerRoom[roomDoc.id] = binsSnapshot.docs.length;
        }

        // Calculate max bins in any room
        const maxBinsInRoom = Math.max(0, ...Object.values(binsPerRoom));

        // Get total items count
        const itemsQuery = query(
          collection(db, 'items'),
          where('roomId', 'in', roomsSnapshot.docs.map(doc => doc.id))
        );
        const itemsSnapshot = await getDocs(itemsQuery);
        const itemsCount = itemsSnapshot.docs.length;

        setStats({
          rooms: roomsCount,
          maxBinsInRoom,
          totalItems: itemsCount,
          binsPerRoom
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching usage stats:', error);
        setLoading(false);
      }
    };

    if (user) {
      fetchStats();
    }
  }, [user]);

  if (loading) return <Container>Loading...</Container>;

  return (
    <Container>
      <Header>
        <Title>Usage Statistics</Title>
        <p>Track your storage usage and limits</p>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatValue $isLimit={!isPremium && stats.rooms >= 2}>
            {stats.rooms}
          </StatValue>
          <StatLabel>Rooms</StatLabel>
          {!isPremium && (
            <LimitIndicator>
              Limit: 2 rooms
            </LimitIndicator>
          )}
        </StatCard>

        <StatCard>
          <StatValue $isLimit={!isPremium && stats.maxBinsInRoom >= 5}>
            {stats.maxBinsInRoom}
          </StatValue>
          <StatLabel>Maximum Bins in a Room</StatLabel>
          {!isPremium && (
            <LimitIndicator>
              Limit: 5 bins per room
            </LimitIndicator>
          )}
        </StatCard>

        <StatCard>
          <StatValue>
            {stats.totalItems}
          </StatValue>
          <StatLabel>Total Items</StatLabel>
          {!isPremium && (
            <LimitIndicator>
              No limit on items
            </LimitIndicator>
          )}
        </StatCard>
      </StatsGrid>

      {!isPremium && (
        <UpgradeSection>
          <h2>Need More Storage?</h2>
          <p>Upgrade to Premium for unlimited rooms and bins</p>
          <UpgradeButton
            variant="primary"
            onClick={() => navigate('/pricing')}
          >
            <PiCrownSimpleFill />
            Upgrade to Premium
          </UpgradeButton>
        </UpgradeSection>
      )}
    </Container>
  );
};

export default Usage; 